import { useEffect, useState, useMemo, useCallback } from "react";
import type { Job, CustomQuoteItem, LayoutType, EnclosureType, WallMeasurement, StructuralChanges } from "@/data/types";
import { calculatePrice, type ComplexityFactors, type PriceBreakdown, type PricingOverrides } from "@/data/pricingEngine";
import { computePricingStatus, PRICING_STATUS_STYLES, type PricingGuardrails } from "@/data/quoteTypes";
import { useProductOptions, CATEGORY_LABELS, type ProductOptionCategory } from "@/data/ProductOptionsContext";
import { useCatalog } from "@/data/CatalogContext";
import { usePricing } from "@/data/PricingContext";
import { generateMaterialListAudit } from "@/data/skuMapping";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft, ArrowRight, Check, ClipboardList, Ruler, Package,
  FileText, Sparkles, Layers, Bath, Grid2X2, ShowerHead,
  Wrench, Plus, Trash2, AlertTriangle, Eye, Square, Triangle,
  LayoutDashboard, Hammer, Paintbrush,
} from "lucide-react";

/* ══════════════════════════════════════════════════════════
   Re-exports for backward compatibility
   ══════════════════════════════════════════════════════════ */
export type { ProjectConfiguration, CustomQuoteItem } from "@/data/types";
export type { BathroomMaterial, BathroomScopeOption, BathroomPhase2Addon, BathroomSystemType, BathroomBaseSize, ProjectVertical } from "@/data/types";
import type { ProjectConfiguration, BathroomMaterial } from "@/data/types";

/* ══════════════════════════════════════════════════════════
   Step definitions — constraint-driven flow
   ══════════════════════════════════════════════════════════ */

interface ConfigStep {
  id: string;
  label: string;
  icon: React.ElementType;
  description: string;
}

const STEPS: ConfigStep[] = [
  { id: "currentState", label: "Current State", icon: Eye, description: "Establish the physical site constraints" },
  { id: "measurements", label: "Measurements", icon: Ruler, description: "Record per-wall dimensions" },
  { id: "structural", label: "Structural Changes", icon: Hammer, description: "Define what is being modified" },
  { id: "projectType", label: "Project Type", icon: LayoutDashboard, description: "Auto-derived from site conditions" },
  { id: "design", label: "Design", icon: Paintbrush, description: "Select materials, enclosure, and fixtures" },
  { id: "review", label: "Review & Materials", icon: Package, description: "Auto-generated material summary" },
];

/* ══════════════════════════════════════════════════════════
   Layout & Obstruction definitions
   ══════════════════════════════════════════════════════════ */

const LAYOUT_OPTIONS: { id: LayoutType; label: string; description: string; icon: React.ElementType; wallCount: number }[] = [
  { id: "Alcove", label: "Alcove (3-Wall)", description: "Standard 3-wall enclosed space", icon: Square, wallCount: 3 },
  { id: "Corner", label: "Corner (2-Wall)", description: "Two walls meeting at a corner", icon: Triangle, wallCount: 2 },
  { id: "Custom", label: "Custom / Open", description: "Non-standard layout or open configuration", icon: Grid2X2, wallCount: 0 },
];

const OBSTRUCTION_OPTIONS = ["Bench", "Niche", "Soffit", "Ledge", "Pipe Chase"];

const ENCLOSURE_OPTIONS: { id: EnclosureType; label: string; description: string }[] = [
  { id: "Frameless Glass", label: "Frameless Glass", description: "Premium frameless glass enclosure" },
  { id: "Semi-Frameless Glass", label: "Semi-Frameless Glass", description: "Partial frame glass enclosure" },
  { id: "Sliding Glass", label: "Sliding Glass", description: "Space-saving sliding glass door" },
  { id: "Curtain Rod", label: "Curtain Rod", description: "Tension or wall-mounted curtain rod" },
  { id: "Open", label: "Open (No Enclosure)", description: "No door or enclosure installed" },
];

const BATHROOM_MATERIALS: { id: BathroomMaterial; label: string; subtitle: string; description: string; color: string }[] = [
  { id: "Sentrel", label: "Sentrel", subtitle: "PVC Composite", description: "Durable, waterproof wall panels. Fast install.", color: "text-info bg-info/10 border-info/30" },
  { id: "Dekton", label: "Dekton", subtitle: "Porcelain Composite", description: "Premium ultra-compact surface. High-end finish.", color: "text-muted bg-muted/10 border-muted/30" },
  { id: "Tile", label: "Tile", subtitle: "Ceramic / Porcelain", description: "Traditional tile with wide variety of styles.", color: "text-warning bg-warning/10 border-warning/30" },
];

/* ══════════════════════════════════════════════════════════
   Auto Project Type derivation
   ══════════════════════════════════════════════════════════ */

function deriveProjectType(config: ProjectConfiguration): string {
  const sc = config.structuralChanges;
  const hasStructural = sc.removingWalls || sc.addingWalls || sc.subfloorMod;
  const hasDrainOrPlumbing = sc.movingDrain || sc.movingPlumbing;

  // If removing/adding walls → Custom Remodel
  if (hasStructural) return "Full Bathroom Remodel";
  // If moving drain → likely tub-to-shower conversion
  if (hasDrainOrPlumbing) return "Tub to Shower Conversion";
  // Default: standard remodel
  return "Shower Remodel";
}

/* ══════════════════════════════════════════════════════════
   Auto Material Generation
   ══════════════════════════════════════════════════════════ */

interface GeneratedMaterial {
  category: string;
  item: string;
  sku: string;
  quantity: number;
  unit: string;
  source: string;
  mappingStatus: "mapped" | "unmapped" | "unverified";
}

function generateMaterialsFromConfig(
  config: ProjectConfiguration,
  allOptions: { id: string; displayName: string; category: string; skuPlaceholder?: string }[],
  knownSkus: Set<string>,
  mappedSkusByRules: Set<string>,
  knownOptionSkus: Set<string>,
  catalogSkuByName: Map<string, string>,
): GeneratedMaterial[] {
  const materials: GeneratedMaterial[] = [];
  const wallA = config.wallMeasurements.wallA;
  const wallB = config.wallMeasurements.wallB;
  const wallC = config.wallMeasurements.wallC;

  const resolveOption = (id: string) => allOptions.find((o) => o.id === id);
  const resolveOptionByName = (name: string, category?: string) => {
    const target = name.trim().toLowerCase();
    const byExactInCategory = allOptions.find((o) =>
      (!category || o.category === category) &&
      o.displayName.trim().toLowerCase() === target
    );
    if (byExactInCategory) return byExactInCategory;
    const byIncludesInCategory = allOptions.find((o) =>
      (!category || o.category === category) &&
      (o.displayName.trim().toLowerCase().includes(target) || target.includes(o.displayName.trim().toLowerCase()))
    );
    if (byIncludesInCategory) return byIncludesInCategory;
    return allOptions.find((o) =>
      o.displayName.trim().toLowerCase() === target ||
      o.displayName.trim().toLowerCase().includes(target) ||
      target.includes(o.displayName.trim().toLowerCase())
    );
  };
  const asAutoSku = (label: string) => `OTH-AUTO-${label.toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`;
  const normalizeSku = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
  const normalizeText = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, " ");
  const resolveCatalogSkuByName = (label: string): string => {
    const normalized = normalizeText(label);
    if (!normalized) return "";
    const exact = catalogSkuByName.get(normalized);
    if (exact) return exact;
    for (const [nameKey, sku] of catalogSkuByName.entries()) {
      if (nameKey.includes(normalized) || normalized.includes(nameKey)) return sku;
    }
    return "";
  };
  const isMappedSku = (sku: string): "mapped" | "unmapped" | "unverified" => {
    if (knownSkus.size === 0) return "unverified";
    const normalized = normalizeSku(sku);
    if (!normalized.length) return "unmapped";
    return knownSkus.has(normalized) || mappedSkusByRules.has(normalized) || knownOptionSkus.has(normalized)
      ? "mapped"
      : "unmapped";
  };

  // Wall panels
  const wallCount = config.wallCount || 3;
  const walls = [wallA, wallB, ...(wallCount >= 3 ? [wallC] : [])].filter(w => w.width && w.height);
  const totalWallSqFt = walls.reduce((sum, w) => sum + (parseFloat(w.width) || 0) * (parseFloat(w.height) || 0) / 144, 0);

  if (config.selectedWallSystem) {
    const opt = resolveOption(config.selectedWallSystem);
    const name = opt?.displayName || "Wall System";
    materials.push({
      category: "Wall System",
      item: name,
      sku: String(opt?.skuPlaceholder ?? "").trim(),
      quantity: Math.ceil(totalWallSqFt),
      unit: "sq ft",
      source: "Design Selection",
      mappingStatus: isMappedSku(String(opt?.skuPlaceholder ?? "").trim()),
    });
  }
  if (config.bathroomMaterials.length > 0) {
    const wallMaterialOption = resolveOptionByName(config.bathroomMaterials[0] ?? "", "wall_system");
    const fallbackWallSystemOption = config.selectedWallSystem ? resolveOption(config.selectedWallSystem) : undefined;
    const wallMaterialSku = String(
      wallMaterialOption?.skuPlaceholder ??
      fallbackWallSystemOption?.skuPlaceholder ??
      ""
    ).trim();
    materials.push({
      category: "Wall Material",
      item: config.bathroomMaterials.join(", "),
      sku: wallMaterialSku,
      quantity: Math.ceil(totalWallSqFt),
      unit: "sq ft",
      source: "Design Selection",
      mappingStatus: isMappedSku(wallMaterialSku),
    });
  }

  // Base / Pan
  if (config.baseType || config.selectedBase) {
    const baseOpt = config.selectedBase ? resolveOption(config.selectedBase) : undefined;
    const baseName = config.selectedBase ? (baseOpt?.displayName || config.baseType) : config.baseType;
    materials.push({
      category: "Base / Pan",
      item: baseName || "Standard Base",
      sku: String(baseOpt?.skuPlaceholder ?? "").trim(),
      quantity: 1,
      unit: "ea",
      source: "Design Selection",
      mappingStatus: isMappedSku(String(baseOpt?.skuPlaceholder ?? "").trim()),
    });
  }

  // Enclosure
  if (config.enclosureType && config.enclosureType !== "Open") {
    const enclosureOption = resolveOptionByName(config.enclosureType, "enclosure");
    const enclosureSku = String(
      enclosureOption?.skuPlaceholder ??
      resolveCatalogSkuByName(config.enclosureType) ??
      ""
    ).trim();
    materials.push({
      category: "Enclosure",
      item: config.enclosureType,
      sku: enclosureSku,
      quantity: 1,
      unit: "ea",
      source: "Design Selection",
      mappingStatus: isMappedSku(enclosureSku),
    });
  }

  // Glass
  if (config.selectedGlass) {
    const opt = resolveOption(config.selectedGlass);
    const name = opt?.displayName || "Glass";
    materials.push({
      category: "Glass",
      item: name,
      sku: String(opt?.skuPlaceholder ?? "").trim(),
      quantity: 1,
      unit: "ea",
      source: "Design Selection",
      mappingStatus: isMappedSku(String(opt?.skuPlaceholder ?? "").trim()),
    });
  }

  // Door
  if (config.selectedDoor) {
    const opt = resolveOption(config.selectedDoor);
    const name = opt?.displayName || "Door";
    materials.push({
      category: "Door",
      item: name,
      sku: String(opt?.skuPlaceholder ?? "").trim(),
      quantity: 1,
      unit: "ea",
      source: "Design Selection",
      mappingStatus: isMappedSku(String(opt?.skuPlaceholder ?? "").trim()),
    });
  }

  // Fixtures
  if (config.selectedFixture) {
    const opt = resolveOption(config.selectedFixture);
    const name = opt?.displayName || "Fixture Package";
    materials.push({
      category: "Fixtures",
      item: name,
      sku: String(opt?.skuPlaceholder ?? "").trim(),
      quantity: 1,
      unit: "set",
      source: "Design Selection",
      mappingStatus: isMappedSku(String(opt?.skuPlaceholder ?? "").trim()),
    });
  }

  // Grab bars
  config.selectedGrabBars.forEach(id => {
    const opt = resolveOption(id);
    const name = opt?.displayName || "Grab Bar";
    materials.push({
      category: "Accessories",
      item: name,
      sku: String(opt?.skuPlaceholder ?? "").trim(),
      quantity: 1,
      unit: "ea",
      source: "Design Selection",
      mappingStatus: isMappedSku(String(opt?.skuPlaceholder ?? "").trim()),
    });
  });

  // Add-ons
  config.selectedAddOns.forEach(id => {
    const opt = resolveOption(id);
    const name = opt?.displayName || "Add-on";
    materials.push({
      category: "Add-ons",
      item: name,
      sku: String(opt?.skuPlaceholder ?? "").trim(),
      quantity: 1,
      unit: "ea",
      source: "Design Selection",
      mappingStatus: isMappedSku(String(opt?.skuPlaceholder ?? "").trim()),
    });
  });

  // Auto-bundles (always included)
  materials.push(
    { category: "Installation Supplies", item: "Silicone Sealant", sku: asAutoSku("Silicone Sealant"), quantity: 2, unit: "tube", source: "Auto-Bundle", mappingStatus: isMappedSku(asAutoSku("Silicone Sealant")) },
    { category: "Installation Supplies", item: "Waterproof Membrane", sku: asAutoSku("Waterproof Membrane"), quantity: Math.ceil(totalWallSqFt * 0.1) || 1, unit: "sq ft", source: "Auto-Bundle", mappingStatus: isMappedSku(asAutoSku("Waterproof Membrane")) },
    { category: "Installation Supplies", item: "Adhesive / Thin-set", sku: asAutoSku("Adhesive Thin-set"), quantity: Math.ceil(totalWallSqFt * 0.05) || 1, unit: "bag", source: "Auto-Bundle", mappingStatus: isMappedSku(asAutoSku("Adhesive Thin-set")) },
    { category: "Installation Supplies", item: "Trim & Edge Pieces", sku: asAutoSku("Trim Edge Pieces"), quantity: wallCount * 2, unit: "ea", source: "Auto-Bundle", mappingStatus: isMappedSku(asAutoSku("Trim Edge Pieces")) },
    { category: "Installation Supplies", item: "Drain Assembly", sku: asAutoSku("Drain Assembly"), quantity: 1, unit: "ea", source: "Auto-Bundle", mappingStatus: isMappedSku(asAutoSku("Drain Assembly")) },
  );

  // Structural supplies
  if (config.structuralChanges.movingDrain || config.structuralChanges.movingPlumbing) {
    materials.push({ category: "Plumbing", item: "Plumbing Relocation Kit", sku: "PRE-001", quantity: 1, unit: "kit", source: "Structural", mappingStatus: isMappedSku("PRE-001") });
  }
  if (config.structuralChanges.subfloorMod) {
    materials.push({ category: "Structural", item: "Subfloor Repair Materials", sku: asAutoSku("Subfloor Repair Materials"), quantity: 1, unit: "kit", source: "Structural", mappingStatus: isMappedSku(asAutoSku("Subfloor Repair Materials")) });
  }

  return materials;
}

/* ══════════════════════════════════════════════════════════
   Default Configuration
   ══════════════════════════════════════════════════════════ */

const DEFAULT_WALL: WallMeasurement = { width: "", height: "" };
const DEFAULT_STRUCTURAL: StructuralChanges = {
  removingWalls: false, addingWalls: false, movingDrain: false,
  movingPlumbing: false, subfloorMod: false, windowMod: "N/A",
};

export const DEFAULT_CONFIG: ProjectConfiguration = {
  layoutType: "", wallCount: 3, hasWindow: false, obstructions: [],
  wallMeasurements: { wallA: { ...DEFAULT_WALL }, wallB: { ...DEFAULT_WALL }, wallC: { ...DEFAULT_WALL }, additional: [] },
  structuralChanges: { ...DEFAULT_STRUCTURAL },
  autoProjectType: "",
  enclosureType: "",
  selectedVerticals: ["Bathroom Project"],
  bathroomScopes: [], bathroomPhase2Addons: [], bathroomMaterials: [],
  bathroomSystemType: "", bathroomBaseSize: "", bathroomCustomSize: "",
  baseWidth: "30", baseLength: "60", baseType: "", baseStyle: "",
  laborComplexity: "standard", plumbingRelocation: false, customModifications: false,
  manualAdjustment: "", manualAdjustmentNotes: "",
  selectedWallSystem: "", selectedBase: "", selectedGrabBars: [],
  selectedGlass: "", selectedDoor: "", selectedFixture: "", selectedAddOns: [],
  customQuoteItems: [], projectCategory: "", workDescription: "",
  areas: [], totalSqFt: "", roomCount: "", measurementNotes: "",
  primaryMaterial: "", materialGrade: "", colorSelection: "",
  wallColor: "", baseColor: "", drainLocation: "",
  additionalOptions: [], basePrice: "", discount: "", discountType: "percent",
  hardwareFinish: "", hardwareOverrides: {}, discounts: { military: false, senior: false, frontliner: false },
  customAddOns: [], noAddOnsSelected: false, consultationCategory: "",
  fullBathroomSelections: { flooring: "", toilet: "", vanity: "" },
  fullBathroomAddOns: [], fullBathroomCustomAddOns: [],
  windowCount: 0, windowTypes: [], doorEntryType: "", doorSize: "",
  additionalCharges: "", additionalChargesNote: "",
  specialConditions: "", customerRequests: "", internalNotes: "",
  status: "draft", lastUpdated: new Date().toISOString(),
};

/* ══════════════════════════════════════════════════════════
   Step Validation
   ══════════════════════════════════════════════════════════ */

function isStepComplete(stepId: string, config: ProjectConfiguration): boolean {
  switch (stepId) {
    case "currentState":
      return !!config.layoutType;
    case "measurements": {
      const wA = config.wallMeasurements.wallA;
      const wB = config.wallMeasurements.wallB;
      return !!(wA.width && wA.height && wB.width && wB.height && config.drainLocation);
    }
    case "structural":
      return true; // All defaults are valid
    case "projectType":
      return true; // Auto-derived
    case "design":
      return config.bathroomMaterials.length > 0;
    case "review":
      return true;
    default:
      return true;
  }
}

function getStepWarnings(stepId: string, config: ProjectConfiguration): string[] {
  const warnings: string[] = [];
  if (stepId === "design") {
    if (!config.selectedWallSystem) warnings.push("No wall system selected");
    if (!config.baseType) warnings.push("No base type selected");
    if (!config.enclosureType) warnings.push("No enclosure type selected");
    if (!config.selectedFixture) warnings.push("No fixture package selected");
  }
  if (stepId === "measurements") {
    if (!config.drainLocation) warnings.push("Drain location is required");
    if (config.wallCount >= 3 && (!config.wallMeasurements.wallC.width || !config.wallMeasurements.wallC.height)) {
      warnings.push("Wall C measurements incomplete for 3-wall layout");
    }
  }
  return warnings;
}

/* ══════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════ */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  config: ProjectConfiguration;
  onSave: (config: ProjectConfiguration) => void;
  onDraftChange?: (config: ProjectConfiguration) => void;
  pricingGuardrails?: PricingGuardrails;
  renderMode?: "sheet" | "inline";
  /** Wizard-level navigation — shown on the Review & Materials (last) step only */
  onWizardBack?: () => void;
  onWizardNext?: () => void;
}

export function ProjectConfigurator({ open, onOpenChange, job, config, onSave, pricingGuardrails, onDraftChange, renderMode = "sheet", onWizardBack, onWizardNext }: Props) {
  const { getByCategory, options: allOptions } = useProductOptions();
  const { skuCatalog, mappingRules } = useCatalog();
  const buildDraftFromConfig = useCallback((incoming: ProjectConfiguration): ProjectConfiguration => ({
    ...DEFAULT_CONFIG,
    ...incoming,
    wallMeasurements: incoming.wallMeasurements || DEFAULT_CONFIG.wallMeasurements,
    structuralChanges: incoming.structuralChanges || DEFAULT_CONFIG.structuralChanges,
  }), []);
  const areConfigsEqual = useCallback((a: ProjectConfiguration, b: ProjectConfiguration): boolean => {
    return JSON.stringify(a) === JSON.stringify(b);
  }, []);
  const knownSkuSet = useMemo(
    () =>
      new Set(
        skuCatalog
          .map((row) => String(row.sku ?? "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, ""))
          .filter((value) => value.length > 0),
      ),
    [skuCatalog],
  );
  const knownOptionSkuSet = useMemo(
    () =>
      new Set(
        allOptions
          .map((o) => String(o.skuPlaceholder ?? "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, ""))
          .filter((value) => value.length > 0),
      ),
    [allOptions],
  );
  const catalogSkuByName = useMemo(
    () =>
      new Map(
        skuCatalog
          .map((row) => [String(row.itemName ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " "), String(row.sku ?? "").trim()] as const)
          .filter(([name, sku]) => name.length > 0 && sku.length > 0),
      ),
    [skuCatalog],
  );
  const { config: pricingConfig } = usePricing();
  const [currentStep, setCurrentStep] = useState(0);
  const [draft, setDraft] = useState<ProjectConfiguration>(() => buildDraftFromConfig(config));
  const mappedSkuSetByRules = useMemo(() => {
    const audit = generateMaterialListAudit(draft, mappingRules, skuCatalog);
    return new Set(
      audit.requirements
        .map((row) => String(row.sku ?? "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, ""))
        .filter((value) => value.length > 0),
    );
  }, [draft, mappingRules, skuCatalog]);

   useEffect(() => {
     const nextDraft = buildDraftFromConfig(config);
     setDraft((prev) => (areConfigsEqual(prev, nextDraft) ? prev : nextDraft));
   }, [config, buildDraftFromConfig, areConfigsEqual]);

   // Notify parent of draft changes for real-time delta tracking
   useEffect(() => {
     onDraftChange?.(draft);
   }, [draft, onDraftChange]);

   const step = STEPS[currentStep];
   const progress = ((currentStep + 1) / STEPS.length) * 100;

  const update = <K extends keyof ProjectConfiguration>(key: K, value: ProjectConfiguration[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const canAdvance = isStepComplete(step.id, draft);
  const stepWarnings = getStepWarnings(step.id, draft);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canAdvance) {
      // Auto-derive project type when leaving structural step
      if (step.id === "structural") {
        const autoType = deriveProjectType(draft);
        setDraft(prev => ({
          ...prev,
          autoProjectType: autoType,
          // Sync complexity from structural
          plumbingRelocation: prev.structuralChanges.movingDrain || prev.structuralChanges.movingPlumbing,
          customModifications: prev.structuralChanges.removingWalls || prev.structuralChanges.addingWalls || prev.structuralChanges.subfloorMod,
          laborComplexity: (prev.structuralChanges.removingWalls || prev.structuralChanges.addingWalls) ? "high" : prev.structuralChanges.movingDrain ? "moderate" : "standard",
        }));
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  /* ── Pricing ── */
  const pricingOverrides: PricingOverrides = {
    systemBasePrices: pricingConfig.systemBasePrices,
    dimensionPresets: pricingConfig.dimensionPresets,
    materialMultipliers: pricingConfig.materialMultipliers,
    complexityAdders: pricingConfig.complexityAdders,
  };
  const complexity: ComplexityFactors = {
    laborComplexity: draft.laborComplexity || "standard",
    plumbingRelocation: draft.plumbingRelocation || false,
    customModifications: draft.customModifications || false,
  };
  const manualAdj = parseFloat(draft.manualAdjustment) || 0;
  const priceBreakdown = useMemo(() =>
    calculatePrice(draft, allOptions, complexity, manualAdj, draft.manualAdjustmentNotes || "", pricingOverrides),
    [draft, allOptions, complexity.laborComplexity, complexity.plumbingRelocation, complexity.customModifications, manualAdj]
  );

  const handleFinalize = () => {
    const autoType = deriveProjectType(draft);
    const final: ProjectConfiguration = {
      ...draft,
      autoProjectType: autoType,
      basePrice: String(priceBreakdown.finalTotal),
      status: "draft",
      lastUpdated: new Date().toISOString(),
    };
    onSave(final);
    onOpenChange(false);
  };

  /* ── Material filter for design ── */
  const materialFilter = draft.bathroomMaterials.length === 1 ? draft.bathroomMaterials[0] : undefined;

  const toggleBuildArray = (key: "selectedGrabBars" | "selectedAddOns" | "obstructions", id: string) => {
    setDraft((prev) => {
      const arr = prev[key] as string[];
      return { ...prev, [key]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] };
    });
  };

  /* ══════════════════════════════════════════════════════════
     STEP 1 — Current State
     ══════════════════════════════════════════════════════════ */
  const renderCurrentState = () => (
    <div className="space-y-6">
      {/* Layout Type */}
      <div>
        <Label className="text-xs font-semibold mb-3 block">Layout Type</Label>
        <div className="grid grid-cols-1 gap-3">
          {LAYOUT_OPTIONS.map(lo => {
            const Icon = lo.icon;
            const selected = draft.layoutType === lo.id;
            return (
              <button key={lo.id} type="button"
                onClick={() => {
                  setDraft(prev => ({
                    ...prev,
                    layoutType: lo.id,
                    wallCount: lo.wallCount || prev.wallCount,
                  }));
                }}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-all ${
                  selected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                }`}>
                <div className={`p-2.5 rounded-lg border transition-colors ${selected ? "text-primary bg-primary/10 border-primary/30" : "text-muted-foreground bg-muted/30 border-transparent"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <span className="text-sm font-semibold block">{lo.label}</span>
                  <span className="text-[11px] text-muted-foreground">{lo.description}</span>
                </div>
                <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "border-primary bg-primary/40" : "border-muted-foreground/30"}`}>
                  {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom wall count */}
      {draft.layoutType === "Custom" && (
        <div>
          <Label className="text-xs font-semibold mb-2 block">Number of Walls</Label>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map(n => (
              <button key={n} type="button" onClick={() => update("wallCount", n)}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  draft.wallCount === n ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}>
                <span className="text-sm font-bold">{n}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Window */}
      <div>
        <Label className="text-xs font-semibold mb-2 block">Window in shower space?</Label>
        <div className="grid grid-cols-2 gap-3">
          {[true, false].map(val => (
            <button key={String(val)} type="button" onClick={() => update("hasWindow", val)}
              className={`p-3 rounded-lg border-2 text-center transition-all ${
                draft.hasWindow === val ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
              }`}>
              <span className="text-sm font-medium">{val ? "Yes" : "No"}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Obstructions */}
      <div>
        <Label className="text-xs font-semibold mb-2 block">Obstructions (optional)</Label>
        <div className="grid grid-cols-2 gap-2">
          {OBSTRUCTION_OPTIONS.map(obs => {
            const selected = draft.obstructions.includes(obs);
            return (
              <button key={obs} type="button" onClick={() => toggleBuildArray("obstructions", obs)}
                className={`flex items-center gap-2 p-2.5 rounded-lg border-2 text-left transition-all ${
                  selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                }`}>
                <div className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${selected ? "bg-primary border-primary" : "border-input"}`}>
                  {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <span className="text-xs font-medium">{obs}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      {draft.layoutType && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-3">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Site Summary</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Layout</span><span className="font-medium">{draft.layoutType}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Walls</span><span className="font-medium">{draft.wallCount}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Window</span><span className="font-medium">{draft.hasWindow ? "Yes" : "No"}</span></div>
              {draft.obstructions.length > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Obstructions</span><span className="font-medium">{draft.obstructions.join(", ")}</span></div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     STEP 2 — Measurements (per-wall)
     ══════════════════════════════════════════════════════════ */
  const updateWall = (wall: "wallA" | "wallB" | "wallC", field: keyof WallMeasurement, value: string) => {
    setDraft(prev => ({
      ...prev,
      wallMeasurements: {
        ...prev.wallMeasurements,
        [wall]: { ...prev.wallMeasurements[wall], [field]: value },
      },
    }));
  };

  const SIZE_PRESETS = [
    { label: "30×60", baseWidth: "30", baseLength: "60", wallA: { width: "30", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "30", height: "96" } },
    { label: "32×60", baseWidth: "32", baseLength: "60", wallA: { width: "32", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "32", height: "96" } },
    { label: "34×60", baseWidth: "34", baseLength: "60", wallA: { width: "34", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "34", height: "96" } },
    { label: "36×60", baseWidth: "36", baseLength: "60", wallA: { width: "36", height: "96" }, wallB: { width: "60", height: "96" }, wallC: { width: "36", height: "96" } },
    { label: "32×40", baseWidth: "32", baseLength: "40", wallA: { width: "32", height: "96" }, wallB: { width: "40", height: "96" }, wallC: { width: "32", height: "96" } },
    { label: "30×40", baseWidth: "30", baseLength: "40", wallA: { width: "30", height: "96" }, wallB: { width: "40", height: "96" }, wallC: { width: "30", height: "96" } },
  ] as const;

  const applyPreset = (preset: typeof SIZE_PRESETS[number]) => {
    setDraft(prev => ({
      ...prev,
      baseWidth: preset.baseWidth,
      baseLength: preset.baseLength,
      wallMeasurements: {
        ...prev.wallMeasurements,
        wallA: { ...prev.wallMeasurements.wallA, ...preset.wallA },
        wallB: { ...prev.wallMeasurements.wallB, ...preset.wallB },
        wallC: { ...prev.wallMeasurements.wallC, ...preset.wallC },
      },
    }));
  };

  const renderMeasurements = () => {
    const wallLabels = ["Wall A (Back)", "Wall B (Left)", "Wall C (Right)"];
    const wallKeys: ("wallA" | "wallB" | "wallC")[] = ["wallA", "wallB", "wallC"];
    const visibleWalls = wallKeys.slice(0, Math.min(draft.wallCount, 3));

    return (
      <div className="space-y-5">
        <p className="text-xs text-muted-foreground">Enter width and height for each wall. All measurements in inches.</p>

        {/* Size presets */}
        <div>
          <Label className="text-xs font-semibold mb-2 block">Size Presets</Label>
          <div className="flex flex-wrap gap-2">
            {SIZE_PRESETS.map(preset => {
              const isActive = draft.baseWidth === preset.baseWidth && draft.baseLength === preset.baseLength;
              return (
                <button key={preset.label} type="button" onClick={() => applyPreset(preset)}
                  className={`px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                    isActive ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-muted-foreground/40 text-muted-foreground hover:text-foreground"
                  }`}>
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Base footprint */}
        <div>
          <Label className="text-xs font-semibold mb-2 block">Base Footprint</Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Width (in)</Label>
              <Input type="number" className="mt-0.5 h-9 text-xs" placeholder="e.g. 30"
                value={draft.baseWidth} onChange={e => update("baseWidth", e.target.value)} />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Depth (in)</Label>
              <Input type="number" className="mt-0.5 h-9 text-xs" placeholder="e.g. 60"
                value={draft.baseLength} onChange={e => update("baseLength", e.target.value)} />
            </div>
          </div>
        </div>

        {/* Per-wall inputs */}
        {visibleWalls.map((wk, i) => {
          const wall = draft.wallMeasurements[wk];
          return (
            <div key={wk} className="rounded-lg border p-3 space-y-2">
              <Label className="text-xs font-semibold">{wallLabels[i]}</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Width (in)</Label>
                  <Input type="number" className="mt-0.5 h-9 text-xs" placeholder="Width"
                    value={wall.width} onChange={e => updateWall(wk, "width", e.target.value)} />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Height (in)</Label>
                  <Input type="number" className="mt-0.5 h-9 text-xs" placeholder="Height"
                    value={wall.height} onChange={e => updateWall(wk, "height", e.target.value)} />
                </div>
              </div>
            </div>
          );
        })}

        {/* Drain location */}
        <div>
          <Label className="text-xs font-semibold mb-2 block">Drain Location</Label>
          <div className="grid grid-cols-4 gap-2">
            {["Left", "Center", "Right", "Custom"].map(loc => (
              <button key={loc} type="button" onClick={() => update("drainLocation", loc)}
                className={`p-2.5 rounded-lg border-2 text-center transition-all ${
                  draft.drainLocation === loc ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}>
                <span className="text-xs font-medium">{loc}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold">Measurement Notes</Label>
          <Input className="mt-1 h-9 text-xs" placeholder="Any special notes about dimensions..."
            value={draft.measurementNotes} onChange={e => update("measurementNotes", e.target.value)} />
        </div>
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     STEP 3 — Structural Changes
     ══════════════════════════════════════════════════════════ */
  const updateStructural = (field: keyof StructuralChanges, value: boolean | string) => {
    setDraft(prev => ({
      ...prev,
      structuralChanges: { ...prev.structuralChanges, [field]: value },
    }));
  };

  const renderStructural = () => {
    const booleanToggles: { key: keyof StructuralChanges; label: string; description: string }[] = [
      { key: "removingWalls", label: "Removing walls?", description: "Demolishing existing walls" },
      { key: "addingWalls", label: "Adding / building walls?", description: "Constructing new walls or framing" },
      { key: "movingDrain", label: "Moving drain?", description: "Relocating shower or tub drain" },
      { key: "movingPlumbing", label: "Moving plumbing / valve?", description: "Relocating supply lines or valves" },
      { key: "subfloorMod", label: "Subfloor modification?", description: "Repair or reinforcement of subfloor" },
    ];

    return (
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">Define what structural work is needed. This determines project type and complexity.</p>
        <div className="space-y-2">
          {booleanToggles.map(t => {
            const val = draft.structuralChanges[t.key] as boolean;
            return (
              <button key={t.key} type="button"
                onClick={() => updateStructural(t.key, !val)}
                className={`w-full flex items-center gap-3 p-3.5 rounded-lg border-2 text-left transition-all ${
                  val ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                }`}>
                <div className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${val ? "bg-primary border-primary" : "border-input"}`}>
                  {val && <Check className="h-3 w-3 text-primary-foreground" />}
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold block">{t.label}</span>
                  <span className="text-[10px] text-muted-foreground">{t.description}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Window modification */}
        <div>
          <Label className="text-xs font-semibold mb-2 block">Window modification?</Label>
          <div className="grid grid-cols-3 gap-2">
            {(["Yes", "No", "N/A"] as const).map(val => (
              <button key={val} type="button" onClick={() => updateStructural("windowMod", val)}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  draft.structuralChanges.windowMod === val ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}>
                <span className="text-xs font-medium">{val}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Complexity preview */}
        {(draft.structuralChanges.removingWalls || draft.structuralChanges.movingDrain || draft.structuralChanges.subfloorMod) && (
          <Card className="border-warning/20 bg-warning/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                <span className="text-[10px] font-bold text-warning uppercase tracking-wider">Complexity Impact</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Structural changes will auto-set complexity to{" "}
                <span className="font-semibold">{draft.structuralChanges.removingWalls || draft.structuralChanges.addingWalls ? "High" : "Moderate"}</span>{" "}
                and adjust pricing accordingly.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     STEP 4 — Project Type (auto-derived, read-only)
     ══════════════════════════════════════════════════════════ */
  const renderProjectType = () => {
    const derivedType = draft.autoProjectType || deriveProjectType(draft);
    const sc = draft.structuralChanges;

    const reasons: string[] = [];
    if (sc.removingWalls || sc.addingWalls) reasons.push("Structural wall changes detected");
    if (sc.movingDrain) reasons.push("Drain relocation required");
    if (sc.movingPlumbing) reasons.push("Plumbing relocation required");
    if (sc.subfloorMod) reasons.push("Subfloor modification needed");
    if (reasons.length === 0) reasons.push("No structural changes — standard remodel");

    return (
      <div className="space-y-5">
        <p className="text-xs text-muted-foreground">Project type is automatically determined based on your site conditions and structural changes.</p>

        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Bath className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Derived Project Type</p>
                <h3 className="text-lg font-bold text-foreground">{derivedType}</h3>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Based On</p>
              {reasons.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span>{r}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Site recap */}
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Site Recap</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Layout</span><span className="font-medium">{draft.layoutType || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Walls</span><span className="font-medium">{draft.wallCount}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Base</span><span className="font-medium">{draft.baseWidth}" × {draft.baseLength}"</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Window</span><span className="font-medium">{draft.hasWindow ? "Yes" : "No"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Complexity</span>
                <Badge variant="outline" className="text-[9px] h-4">
                  {(sc.removingWalls || sc.addingWalls) ? "High" : (sc.movingDrain || sc.movingPlumbing) ? "Moderate" : "Standard"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     STEP 5 — Design (constraint-aware)
     ══════════════════════════════════════════════════════════ */

  const renderOptionSelector = (
    label: string, category: ProductOptionCategory, selectedValue: string, onSelect: (id: string) => void,
  ) => {
    const opts = getByCategory(category, materialFilter);
    return (
      <div>
        <Label className="text-xs font-semibold mb-2 block">{label}</Label>
        <div className="space-y-1.5">
          {opts.map(opt => {
            const selected = selectedValue === opt.id;
            return (
              <button key={opt.id} type="button" onClick={() => onSelect(opt.id)}
                className={`w-full flex items-center justify-between p-2.5 rounded-lg border-2 text-left transition-all ${
                  selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                }`}>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium block">{opt.displayName}</span>
                  {opt.notes && <span className="text-[10px] text-muted-foreground">{opt.notes}</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-[10px] font-medium text-muted-foreground">{opt.pricingImpact}</span>
                  <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${selected ? "border-primary bg-primary/40" : "border-muted-foreground/30"}`}>
                    {selected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                  </div>
                </div>
              </button>
            );
          })}
          {opts.length === 0 && <p className="text-xs text-muted-foreground italic p-2">No options available.</p>}
        </div>
      </div>
    );
  };

  const renderMultiOptionSelector = (
    label: string, category: ProductOptionCategory, selectedValues: string[], onToggle: (id: string) => void,
  ) => {
    const opts = getByCategory(category, materialFilter);
    return (
      <div>
        <Label className="text-xs font-semibold mb-2 block">{label}</Label>
        <div className="space-y-1.5">
          {opts.map(opt => {
            const selected = selectedValues.includes(opt.id);
            return (
              <button key={opt.id} type="button" onClick={() => onToggle(opt.id)}
                className={`w-full flex items-center justify-between p-2.5 rounded-lg border-2 text-left transition-all ${
                  selected ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                }`}>
                <div className="flex items-center gap-2 flex-1">
                  <div className={`h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${selected ? "bg-primary border-primary" : "border-input"}`}>
                    {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <div>
                    <span className="text-xs font-medium block">{opt.displayName}</span>
                    {opt.notes && <span className="text-[10px] text-muted-foreground">{opt.notes}</span>}
                  </div>
                </div>
                <span className="text-[10px] font-medium text-muted-foreground shrink-0 ml-2">{opt.pricingImpact}</span>
              </button>
            );
          })}
          {opts.length === 0 && <p className="text-xs text-muted-foreground italic p-2">No options available.</p>}
        </div>
      </div>
    );
  };

  const toggleBathroomMaterial = (m: BathroomMaterial) => {
    setDraft(prev => ({
      ...prev,
      bathroomMaterials: prev.bathroomMaterials.includes(m)
        ? prev.bathroomMaterials.filter(x => x !== m)
        : [...prev.bathroomMaterials, m],
    }));
  };

  const renderDesign = () => {
    const showGlassOptions = draft.enclosureType === "Frameless Glass" || draft.enclosureType === "Semi-Frameless Glass" || draft.enclosureType === "Sliding Glass";

    return (
      <div className="space-y-6">
        {/* Wall Material */}
        <div>
          <Label className="text-xs font-semibold mb-3 block">Wall System Material</Label>
          <div className="space-y-2">
            {BATHROOM_MATERIALS.map(m => {
              const selected = draft.bathroomMaterials.includes(m.id);
              return (
                <button key={m.id} type="button" onClick={() => toggleBathroomMaterial(m.id)}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                    selected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                  }`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "border-primary bg-primary/40" : "border-muted-foreground/30"}`}>
                      {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">{m.label}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${m.color}`}>{m.subtitle}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">{m.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Wall System Product */}
        {renderOptionSelector("Wall System Product", "wall_system", draft.selectedWallSystem, id => update("selectedWallSystem", id))}

        {/* Base */}
        <div>
          <Label className="text-xs font-semibold mb-2 block">Base Type</Label>
          <div className="grid grid-cols-2 gap-2">
            {["Standard Acrylic", "Low-Threshold", "Cast Stone", "Tile-Ready"].map(bt => (
              <button key={bt} type="button" onClick={() => update("baseType", bt)}
                className={`p-2.5 rounded-lg border-2 text-center transition-all ${
                  draft.baseType === bt ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}>
                <span className="text-xs font-medium">{bt}</span>
              </button>
            ))}
          </div>
        </div>

        {renderOptionSelector("Base Product", "base", draft.selectedBase, id => update("selectedBase", id))}

        {/* Enclosure Type (REQUIRED) */}
        <div>
          <Label className="text-xs font-semibold mb-3 block">Enclosure Type <span className="text-destructive">*</span></Label>
          <div className="space-y-2">
            {ENCLOSURE_OPTIONS.map(enc => {
              const selected = draft.enclosureType === enc.id;
              return (
                <button key={enc.id} type="button"
                  onClick={() => update("enclosureType", enc.id as EnclosureType)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                    selected ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/30 hover:bg-muted/30"
                  }`}>
                  <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? "border-primary bg-primary/40" : "border-muted-foreground/30"}`}>
                    {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                  <div>
                    <span className="text-xs font-semibold block">{enc.label}</span>
                    <span className="text-[10px] text-muted-foreground">{enc.description}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Glass options (conditional) */}
        {showGlassOptions && (
          <>
            {renderOptionSelector("Glass", "glass", draft.selectedGlass, id => update("selectedGlass", id))}
            {renderOptionSelector("Door Type", "door", draft.selectedDoor, id => update("selectedDoor", id))}
          </>
        )}

        {/* Fixtures */}
        {renderOptionSelector("Fixture Package", "fixture", draft.selectedFixture, id => update("selectedFixture", id))}

        {/* Accessories */}
        {renderMultiOptionSelector("Grab Bars", "grab_bar", draft.selectedGrabBars, id => toggleBuildArray("selectedGrabBars", id))}
        {renderMultiOptionSelector("Add-ons", "add_on", draft.selectedAddOns, id => toggleBuildArray("selectedAddOns", id))}
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     STEP 6 — Review & Auto Material Generation
     ══════════════════════════════════════════════════════════ */
  const renderReview = () => {
    const derivedType = draft.autoProjectType || deriveProjectType(draft);
    const optName = (id: string) => allOptions.find(o => o.id === id)?.displayName || "—";
    const generatedMaterials = generateMaterialsFromConfig(
      draft,
      allOptions,
      knownSkuSet,
      mappedSkuSetByRules,
      knownOptionSkuSet,
      catalogSkuByName,
    );
    const grouped = generatedMaterials.reduce<Record<string, GeneratedMaterial[]>>((acc, m) => {
      (acc[m.category] ||= []).push(m);
      return acc;
    }, {});

    const configSections = [
      { label: "Site Conditions", items: [
        { k: "Layout", v: draft.layoutType || "—" },
        { k: "Walls", v: String(draft.wallCount) },
        { k: "Window", v: draft.hasWindow ? "Yes" : "No" },
        { k: "Obstructions", v: draft.obstructions.length ? draft.obstructions.join(", ") : "None" },
      ]},
      { label: "Measurements", items: [
        { k: "Base", v: `${draft.baseWidth}" × ${draft.baseLength}"` },
        { k: "Wall A", v: draft.wallMeasurements.wallA.width ? `${draft.wallMeasurements.wallA.width}" × ${draft.wallMeasurements.wallA.height}"` : "—" },
        { k: "Wall B", v: draft.wallMeasurements.wallB.width ? `${draft.wallMeasurements.wallB.width}" × ${draft.wallMeasurements.wallB.height}"` : "—" },
        ...(draft.wallCount >= 3 ? [{ k: "Wall C", v: draft.wallMeasurements.wallC.width ? `${draft.wallMeasurements.wallC.width}" × ${draft.wallMeasurements.wallC.height}"` : "—" }] : []),
        { k: "Drain", v: draft.drainLocation || "—" },
      ]},
      { label: "Structural Changes", items: [
        { k: "Removing Walls", v: draft.structuralChanges.removingWalls ? "Yes" : "No" },
        { k: "Adding Walls", v: draft.structuralChanges.addingWalls ? "Yes" : "No" },
        { k: "Moving Drain", v: draft.structuralChanges.movingDrain ? "Yes" : "No" },
        { k: "Moving Plumbing", v: draft.structuralChanges.movingPlumbing ? "Yes" : "No" },
        { k: "Subfloor Mod", v: draft.structuralChanges.subfloorMod ? "Yes" : "No" },
        { k: "Window Mod", v: draft.structuralChanges.windowMod },
      ]},
      { label: "Project Type", items: [
        { k: "Derived Type", v: derivedType },
      ]},
      { label: "Design Selections", items: [
        { k: "Material", v: draft.bathroomMaterials.length ? draft.bathroomMaterials.join(", ") : "—" },
        { k: "Wall System", v: draft.selectedWallSystem ? optName(draft.selectedWallSystem) : "—" },
        { k: "Base Type", v: draft.baseType || "—" },
        { k: "Enclosure", v: draft.enclosureType || "—" },
        { k: "Glass", v: draft.selectedGlass ? optName(draft.selectedGlass) : "—" },
        { k: "Door", v: draft.selectedDoor ? optName(draft.selectedDoor) : "—" },
        { k: "Fixtures", v: draft.selectedFixture ? optName(draft.selectedFixture) : "—" },
        { k: "Grab Bars", v: draft.selectedGrabBars.length ? draft.selectedGrabBars.map(optName).join(", ") : "—" },
        { k: "Add-ons", v: draft.selectedAddOns.length ? draft.selectedAddOns.map(optName).join(", ") : "—" },
      ]},
    ];

    return (
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">Review configuration and auto-generated material list.</p>

        {/* Config summary */}
        {configSections.map(s => (
          <Card key={s.label} className="overflow-hidden">
            <div className="px-3 py-1.5 bg-muted/30 border-b">
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">{s.label}</span>
            </div>
            <CardContent className="p-3 space-y-1">
              {s.items.map(item => (
                <div key={item.k} className="flex justify-between gap-2 text-xs">
                  <span className="text-muted-foreground shrink-0">{item.k}</span>
                  <span className="font-medium text-right truncate max-w-[60%]">{item.v}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        {/* Validation warnings */}
        {stepWarnings.length > 0 && (
          <Card className="border-warning/30 bg-warning/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                <span className="text-[10px] font-bold text-warning uppercase tracking-wider">Warnings</span>
              </div>
              {getStepWarnings("design", draft).map((w, i) => (
                <p key={i} className="text-xs text-warning">• {w}</p>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Auto-generated material list */}
        <Card className="border-primary/20 overflow-hidden">
          <div className="px-3 py-2 bg-primary/5 border-b flex items-center gap-2">
            <Package className="h-3.5 w-3.5 text-primary" />
            <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Included Materials (Auto-Generated)</span>
          </div>
          <CardContent className="p-3 space-y-3">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">{cat}</p>
                {items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-dashed last:border-0 gap-2">
                    <div className="min-w-0">
                      <div className="truncate">{item.item}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        SKU: {item.sku || "UNMAPPED"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">{item.quantity} {item.unit}</span>
                      <Badge
                        variant={item.mappingStatus === "mapped" ? "outline" : item.mappingStatus === "unmapped" ? "destructive" : "secondary"}
                        className="text-[8px] h-4"
                      >
                        {item.mappingStatus === "mapped" ? "Mapped" : item.mappingStatus === "unmapped" ? "Unmapped" : "Unverified"}
                      </Badge>
                      <Badge variant="outline" className="text-[8px] h-4">{item.source}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     Step router
     ══════════════════════════════════════════════════════════ */
  const renderStep = () => {
    switch (step.id) {
      case "currentState": return renderCurrentState();
      case "measurements": return renderMeasurements();
      case "structural": return renderStructural();
      case "projectType": return renderProjectType();
      case "design": return renderDesign();
      case "review": return renderReview();
      default: return null;
    }
  };

  /* ══════════════════════════════════════════════════════════
     Layout
     ══════════════════════════════════════════════════════════ */
  const innerContent = (
    <div className={renderMode === "inline" ? "flex flex-col min-h-[500px]" : "flex flex-col h-full"}>
      {renderMode === "sheet" && (
        <SheetHeader className="p-4 pb-3 border-b shrink-0">
          <SheetTitle className="text-sm font-bold flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            Project Configurator — {job.id}
          </SheetTitle>
          <p className="text-[11px] text-muted-foreground">{job.customerName}</p>
        </SheetHeader>
      )}

      {/* Step progress */}
      <div className={`${renderMode === "inline" ? "pb-2" : "px-4 pt-3 pb-2 border-b"} shrink-0`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-muted-foreground font-medium">Step {currentStep + 1} of {STEPS.length}</span>
          <span className="text-[10px] text-muted-foreground">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-1.5" />
        <div className="flex flex-wrap gap-1 mt-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === currentStep;
            const isComplete = i < currentStep;
            const canGo = i <= currentStep || (i === currentStep + 1 && canAdvance);
            return (
              <button key={s.id}
                onClick={() => { if (canGo || i < currentStep) setCurrentStep(i); }}
                disabled={!canGo && i > currentStep}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground shadow-sm"
                  : isComplete ? "bg-primary/10 text-primary hover:bg-primary/20"
                  : "bg-muted/50 text-muted-foreground"
                } ${!canGo && i > currentStep ? "opacity-50 cursor-not-allowed" : ""}`}>
                {isComplete ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 ${renderMode === "inline" ? "py-4" : "overflow-y-auto p-4"}`}>
        <div className="flex items-center gap-2 mb-1">
          {(() => { const Icon = step.icon; return <Icon className="h-4 w-4 text-primary" />; })()}
          <h3 className="text-sm font-bold">{step.label}</h3>
        </div>
        <p className="text-[11px] text-muted-foreground mb-4">{step.description}</p>
        {renderStep()}
      </div>

      {/* Footer */}
      <div className={`${renderMode === "inline" ? "pt-4 mt-4 border-t" : "p-4 border-t"} shrink-0 flex items-center justify-between gap-2 bg-muted/20 ${renderMode === "inline" ? "rounded-lg p-3" : ""}`}>
        {/* Left side — internal back OR wizard back on last step */}
        {currentStep === STEPS.length - 1 && onWizardBack ? (
          <Button variant="ghost" size="sm" onClick={onWizardBack} className="gap-1.5 text-muted-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> Customer Info
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={handleBack} disabled={currentStep === 0}>
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
          </Button>
        )}

        {/* Right side */}
        <div className="flex items-center gap-2">
          {!canAdvance && currentStep < STEPS.length - 1 && (
            <span className="text-[10px] text-destructive font-medium">Complete required fields</span>
          )}
          {renderMode === "sheet" && (
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
          )}
          {currentStep < STEPS.length - 1 ? (
            <Button size="sm" onClick={handleNext} disabled={!canAdvance}>
              Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          ) : (
            <>
              <Button size="sm" variant="outline" onClick={handleFinalize}>
                <Check className="h-3.5 w-3.5 mr-1" /> Save Configuration
              </Button>
              {onWizardNext && (
                <Button size="sm" onClick={() => { handleFinalize(); onWizardNext(); }} className="gap-1.5">
                  Save & Continue <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (renderMode === "inline") return open ? innerContent : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl lg:max-w-4xl p-0 flex flex-col">
        {innerContent}
      </SheetContent>
    </Sheet>
  );
}
